import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    Flame,
    Loader2,
    RefreshCw,
    AlertCircle,
    Check,
    MessageSquare,
    Inbox,
    ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/hot-leads`;

const FILTERS = [
    { key: "unack", label: "Unacknowledged" },
    { key: "all", label: "All" },
];

const fmtRelativeTime = (iso) => {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        const diffMs = Date.now() - d.getTime();
        const mins = Math.floor(diffMs / 60000);
        if (mins < 1) return "just now";
        if (mins < 60) return `${mins} min${mins === 1 ? "" : "s"} ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs} hour${hrs === 1 ? "" : "s"} ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
        return d.toLocaleDateString("en-CA", { month: "short", day: "numeric" });
    } catch {
        return iso;
    }
};

const truncate = (s, n = 140) => {
    if (!s) return "";
    if (s.length <= n) return s;
    return `${s.slice(0, n).trim()}…`;
};

const ContextPill = ({ label, value }) => {
    if (!value) return null;
    return (
        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold bg-brand-dark/5 text-brand-dark/80 border border-brand-dark/10">
            <span className="text-brand-gray">{label}:</span>
            <span>{value}</span>
        </span>
    );
};

const ScorePill = ({ score }) => (
    <span
        className={cn(
            "inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold border",
            score >= 9
                ? "bg-brand-coral text-white border-brand-coral"
                : "bg-brand-coral/15 text-brand-coral border-brand-coral/40",
        )}
        data-testid="hot-lead-score-pill"
    >
        <Flame className="h-3 w-3" strokeWidth={2.5} />
        {score}/10
    </span>
);

const HotLeadCard = ({ lead, onAcknowledge, onOpenConversation, acknowledging }) => {
    const ctx = lead.extracted_context || {};
    const services = (ctx.services_interested || []).join(", ") || null;
    return (
        <article
            className={cn(
                "border-l-4 bg-white border border-brand-dark/10 p-5 md:p-6 transition-all duration-300 ease-out",
                lead.acknowledged
                    ? "border-l-brand-dark/20 opacity-60"
                    : "border-l-brand-coral hover:border-brand-coral/40",
            )}
            data-testid={`hot-lead-card-${lead.id}`}
            data-acknowledged={lead.acknowledged ? "true" : "false"}
        >
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-3 flex-wrap">
                    <ScorePill score={lead.score_at_trigger} />
                    <span
                        className="text-xs text-brand-gray"
                        data-testid={`hot-lead-time-${lead.id}`}
                    >
                        {fmtRelativeTime(lead.created_at)}
                    </span>
                    {lead.acknowledged && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] uppercase tracking-wider font-semibold bg-brand-dark/5 text-brand-dark/60 border border-brand-dark/10">
                            <Check className="h-3 w-3" strokeWidth={2.5} />
                            Acknowledged
                        </span>
                    )}
                </div>
                <span className="text-[10px] uppercase tracking-wider text-brand-gray hidden md:inline">
                    {lead.initial_page || "/"}
                </span>
            </div>

            <div
                className="flex flex-wrap gap-2 mb-4"
                data-testid={`hot-lead-context-${lead.id}`}
            >
                <ContextPill label="services" value={services} />
                <ContextPill label="city" value={ctx.city} />
                <ContextPill label="timeline" value={ctx.timeline} />
                <ContextPill label="size" value={ctx.project_size} />
            </div>

            {lead.last_user_message && (
                <blockquote
                    className="border-l-2 border-brand-cyan/40 pl-4 py-1 mb-4 text-sm italic text-brand-dark/85"
                    data-testid={`hot-lead-message-${lead.id}`}
                >
                    &ldquo;{truncate(lead.last_user_message)}&rdquo;
                </blockquote>
            )}

            <div className="flex flex-wrap items-center gap-2">
                {!lead.acknowledged && (
                    <button
                        type="button"
                        onClick={() => onAcknowledge(lead.id)}
                        disabled={acknowledging}
                        className="inline-flex items-center gap-2 bg-brand-coral hover:bg-[#d04438] disabled:opacity-60 text-white font-semibold uppercase tracking-wider text-xs px-4 h-9 transition-colors"
                        data-testid={`hot-lead-ack-${lead.id}`}
                    >
                        {acknowledging ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={2.5} />
                        ) : (
                            <Check className="h-3.5 w-3.5" strokeWidth={2.5} />
                        )}
                        Acknowledge
                    </button>
                )}
                <button
                    type="button"
                    onClick={() => onOpenConversation(lead.chat_session_id)}
                    className="inline-flex items-center gap-2 bg-white hover:border-brand-cyan hover:text-brand-cyan border border-brand-dark/15 text-brand-dark font-semibold uppercase tracking-wider text-xs px-4 h-9 transition-colors"
                    data-testid={`hot-lead-open-${lead.id}`}
                >
                    <MessageSquare className="h-3.5 w-3.5" strokeWidth={2.5} />
                    Open conversation
                    <ExternalLink className="h-3 w-3" strokeWidth={2.5} />
                </button>
            </div>
        </article>
    );
};

const AdminHotLeadsPanel = ({ token, onOpenChat, onAcknowledged }) => {
    const [filter, setFilter] = useState("unack");
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [acking, setAcking] = useState(null);

    const headers = useMemo(() => ({ "X-Admin-Token": token }), [token]);

    const fetchLeads = useCallback(async () => {
        setLoading(true);
        setError("");
        try {
            const params = filter === "unack" ? { acknowledged: false } : {};
            const res = await axios.get(API_BASE, { headers, params });
            setLeads(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setError(
                err?.response?.data?.detail ||
                    "Could not load hot leads. Try refreshing.",
            );
        } finally {
            setLoading(false);
        }
    }, [filter, headers]);

    useEffect(() => {
        fetchLeads();
    }, [fetchLeads]);

    const handleAcknowledge = async (id) => {
        setAcking(id);
        // Optimistic local update — the card collapses to its acknowledged
        // state instantly (200-300ms CSS transition) rather than waiting for
        // the next fetchLeads round-trip.
        const ackedAt = new Date().toISOString();
        setLeads((prev) =>
            prev.map((l) =>
                l.id === id ? { ...l, acknowledged: true, acknowledged_at: ackedAt } : l,
            ),
        );
        // Notify parent so the persistent nav badge decrements at the same time.
        if (typeof onAcknowledged === "function") onAcknowledged(id);
        try {
            await axios.post(`${API_BASE}/${id}/acknowledge`, {}, { headers });
            // If the Unacknowledged filter is active, drop the now-acked card
            // from the visible list after a short transition window so the user
            // gets to see the collapse animation first.
            if (filter === "unack") {
                setTimeout(
                    () => setLeads((prev) => prev.filter((l) => l.id !== id)),
                    400,
                );
            }
        } catch (err) {
            // Roll back the optimistic update on failure.
            setLeads((prev) =>
                prev.map((l) =>
                    l.id === id
                        ? { ...l, acknowledged: false, acknowledged_at: null }
                        : l,
                ),
            );
            setError(
                err?.response?.data?.detail ||
                    "Could not acknowledge that lead. Try again.",
            );
        } finally {
            setAcking(null);
        }
    };

    const handleOpenConversation = (sessionId) => {
        if (onOpenChat) onOpenChat(sessionId);
    };

    const unackCount = useMemo(
        () => leads.filter((l) => !l.acknowledged).length,
        [leads],
    );

    return (
        <div data-testid="admin-hot-leads-panel">
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
                <div>
                    <h2 className="font-display text-3xl tracking-wide text-brand-dark leading-none flex items-center gap-3">
                        <Flame className="h-7 w-7 text-brand-coral" strokeWidth={2.5} />
                        Hot Leads
                    </h2>
                    <p
                        className="mt-2 text-sm text-brand-dark/70"
                        data-testid="hot-leads-summary"
                    >
                        Visitors who hit intent score ≥ 8 in chat.{" "}
                        <span className="font-semibold text-brand-coral">
                            Acknowledge promptly.
                        </span>{" "}
                        <span
                            className="ml-2 inline-flex items-center gap-1 px-2 py-0.5 bg-brand-coral text-white text-xs font-bold"
                            data-testid="hot-leads-unack-count"
                        >
                            {unackCount} pending
                        </span>
                    </p>
                </div>
                <button
                    type="button"
                    onClick={fetchLeads}
                    disabled={loading}
                    className="inline-flex items-center gap-2 bg-white hover:border-brand-cyan hover:text-brand-cyan border border-brand-dark/15 text-brand-dark font-semibold uppercase tracking-wider text-xs px-4 h-10 transition-colors self-start"
                    data-testid="hot-leads-refresh"
                >
                    {loading ? (
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2.5} />
                    ) : (
                        <RefreshCw className="h-4 w-4" strokeWidth={2.5} />
                    )}
                    Refresh
                </button>
            </div>

            <div
                className="flex flex-wrap items-center gap-2 mb-6"
                role="tablist"
                aria-label="Hot lead filters"
            >
                {FILTERS.map((f) => {
                    const active = filter === f.key;
                    return (
                        <button
                            key={f.key}
                            type="button"
                            onClick={() => setFilter(f.key)}
                            role="tab"
                            aria-selected={active}
                            className={cn(
                                "inline-flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider font-semibold transition-colors duration-200 border",
                                active
                                    ? "bg-brand-dark text-white border-brand-dark"
                                    : "bg-white text-brand-dark border-brand-dark/15 hover:border-brand-cyan hover:text-brand-cyan",
                            )}
                            data-testid={`hot-leads-filter-${f.key}`}
                        >
                            {f.label}
                        </button>
                    );
                })}
            </div>

            {error && (
                <div
                    className="mb-6 border-l-4 border-brand-coral bg-brand-coral/10 p-4 text-sm text-brand-dark"
                    data-testid="hot-leads-error"
                >
                    <p className="font-semibold flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-brand-coral" strokeWidth={2.5} />
                        {error}
                    </p>
                </div>
            )}

            {!loading && leads.length === 0 ? (
                <div
                    className="bg-white border-2 border-dashed border-brand-dark/15 p-16 text-center"
                    data-testid="hot-leads-empty"
                >
                    <Inbox className="h-10 w-10 mx-auto text-brand-gray mb-4" strokeWidth={1.5} />
                    <h3 className="font-display text-2xl tracking-wide text-brand-dark mb-2">
                        No hot leads {filter === "unack" ? "pending" : "yet"}.
                    </h3>
                    <p className="text-sm text-brand-dark/60">
                        New high-intent chat conversations will appear here as soon as
                        a visitor crosses score 8.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-4" data-testid="hot-leads-list">
                    {leads.map((lead) => (
                        <HotLeadCard
                            key={lead.id}
                            lead={lead}
                            acknowledging={acking === lead.id}
                            onAcknowledge={handleAcknowledge}
                            onOpenConversation={handleOpenConversation}
                        />
                    ))}
                </div>
            )}
        </div>
    );
};

export default AdminHotLeadsPanel;
